import { useCallback, useLayoutEffect, useRef, useState } from "react";

const VIEWPORT_MARGIN = 8;

/**
 * Keep an absolutely positioned datepicker popover inside the viewport.
 * `alignment` is only a preference: the popover still shifts if it would overflow.
 */
export function getDatepickerPopoverOffsetLeft(
  anchorEl,
  popoverEl,
  preferredAlignment = "left",
) {
  if (!anchorEl || !popoverEl) {
    return 0;
  }

  const anchor = anchorEl.getBoundingClientRect();
  const width = popoverEl.offsetWidth;
  const viewportWidth =
    document.documentElement?.clientWidth || window.innerWidth;
  let left = preferredAlignment === "right" ? anchor.width - width : 0;

  const overflowRight =
    anchor.left + left + width - (viewportWidth - VIEWPORT_MARGIN);
  if (overflowRight > 0) {
    left -= overflowRight;
  }

  const overflowLeft = VIEWPORT_MARGIN - (anchor.left + left);
  if (overflowLeft > 0) {
    left += overflowLeft;
  }

  return left;
}

export function useDatepickerPopoverPosition({
  open,
  preferredAlignment = "left",
  anchorRef,
}) {
  const popoverRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState(
    preferredAlignment === "right" ? { right: 0 } : { left: 0 },
  );

  const update = useCallback(() => {
    const anchor = anchorRef?.current;
    const popover = popoverRef.current;
    if (!open || !anchor || !popover) {
      return;
    }
    setPopoverStyle({
      left: getDatepickerPopoverOffsetLeft(
        anchor,
        popover,
        preferredAlignment,
      ),
    });
  }, [anchorRef, open, preferredAlignment]);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    update();

    const popover = popoverRef.current;
    const observer =
      typeof ResizeObserver !== "undefined" && popover
        ? new ResizeObserver(update)
        : null;
    if (observer && popover) {
      observer.observe(popover);
    }

    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [open, update]);

  return { popoverRef, popoverStyle };
}
